import { Component, ElementRef, Input, Output, ViewChild, EventEmitter } from '@angular/core';
import { FormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Clipboard } from '@angular/cdk/clipboard';
import { ErrorService } from '../service/error.service';
import { Router } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { ToastService } from 'src/app/services/toast.service';

@Component({
    selector: 'nejo-error-retry',
    templateUrl: './error-retry.component.html',
    styleUrls: ['./error-retry.component.scss'],
    standalone: false
})
export class ErrorRetryComponent {
  @Input() json;
  @Input() updated;
  @Input() errorobj;
  @Output() eventErrorRetry = new EventEmitter<any>();

  @ViewChild('textArea', { read: ElementRef }) _textArea !: ElementRef<HTMLTextAreaElement>;
  public form: UntypedFormGroup;

  constructor(
    private readonly modalService: NgbModal,
    private readonly fb: FormBuilder,
    public errorService: ErrorService,
    private readonly clipboard: Clipboard,
    public router: Router,
    public toastService: ToastService,
  ) {
    let json = "";
    if (this.json) {
      json = JSON.stringify(JSON.parse(this.json), undefined, 2);
    }
    this.form = this.fb.group({
      jsonParsedForm: new UntypedFormControl(json, [Validators.required])
    });
  }



  /** getTextAreaElement
   *  Funzione che controlla se textarea è stata inizializzata, se non è stata inizializzata 
   *  la cerca in base alla classe di stile, ne tiene traccia e restituisce l'oggetto,
   *  altrimenti se la trova restituisce l'oggetto senza rieffettuare la ricerca per classe.
   * @returns 
   */
  getTextAreaElement() {
    if (this._textArea) {
      if (this._textArea.nativeElement) {
        return this._textArea.nativeElement;
      }
      return null;
    } else {
      let el = document.getElementsByClassName('textarea-custom');
      if (el.length > 0) {
        this._textArea = new ElementRef(el[0] as HTMLTextAreaElement);
        return this._textArea.nativeElement;
      }
    }
  }

  /** jsonParsedForm
   *  Restituisce il valore della textArea collegata al form con il nome jsonParsedForm
   */
  get jsonParsedForm(): string {
    return this.form.get('jsonParsedForm').value;
  }


  /** setJsonParsedForm
   *  Passando una stringa (json stringify) lo setta sul form nella chiave della text area jsonParsedForm
   * @param value 
   */
  setJsonParsedForm(value: string) {
    if (value) {
      let json = JSON.stringify(JSON.parse(value), undefined, 2);
      this.form.get('jsonParsedForm').setValue(json);
    }
  }


  initTextArea(json: string) {
    this.setJsonParsedForm(json);
    this.cursorPositionStart();
  }

  resetTextArea(json: string) {
    this.setJsonParsedForm(json);
    setTimeout(() => {
      this.cursorPositionStart();
    }, 1000);
  }

  /** cursorPositionStart
   *  Funzione per posizionare il cursore del textArea all'inizio
   */
  cursorPositionStart() {
    let el = this.getTextAreaElement();
    el.focus();
    el.setSelectionRange(0, 0);
  }

  /** closeAndRefresh
   *  chiude la modale ed emette un valore all'output al componente in ascolto
   *  se save è true significa che è partita una chiamata al BE di salvataggio, altrimenti save è false
   * @param value 
   */
  closeAndRefresh(value: boolean) {
    this.modalService.dismissAll();
    let res: any = { save: value };
    this.eventErrorRetry.emit(res);
  }


  /** getBody
  *  restituisce l'oggetto da mandare all'API per il salvataggio
  * @returns il JSON come oggetto oppure un undefined se non può convertire il JSON per qualche problema come
  * JSON non valido
  */
  getBody() {
    try {
      return JSON.parse(this.jsonParsedForm);
    } catch (error) {
      console.log(error);
      return undefined;
    }
  }

  /** save
   *  Funzione che effettua il salvataggio del form
   */
  save() {
    let body = this.getBody();
    if (typeof body === 'undefined') {
      this.toastService.showError("JSON non valido.", 5000);
    } else {
      this.errorService.sendEditJSON(body).subscribe({
        next: (response: any) => {
          console.log(response);
          if (response) {
            this.closeAndRefresh(true);
            // Aggiungo un delay prima di fare un redirect per dare tempo di fare un refresh della tabella
            // ed aggiornare il valore di errors sulla sidebar
            setTimeout(() => {
              this.router.navigate(['order/' + response.identifier]);
            }, 1000);
          }
        },
        error: (error: HttpErrorResponse) => {
          console.log(error);
          let res: any = { save: true };
          this.eventErrorRetry.emit(res);
        }
      });
    }
  }





  /** clickBtnOpenModal
  *   Azione click button apri modale ed inizializza il valore della textarea
  * @param content 
  * @param event 
  */
  clickBtnOpenModal(content, event) {
    try {
      event.target.closest('datatable-body-cell').blur();
    } catch (e) { }
    this.modalService.open(content, { ariaLabelledBy: 'modal-basic-title' });
    this.initTextArea(this.json);
  }

  /** clickBtnCopyText
   *  Azione click button Copia il testo del jsonParsedForm e cambia il messaggio da copy a copied per 1secondo
   */
  messagecopy: string = "Copy";
  clickBtnCopyText() {
    this.clipboard.copy(this.jsonParsedForm);
    this.messagecopy = "Copied";
    setTimeout(() => { this.messagecopy = "Copy"; }, 1000);
  }

  /** clickBtnFormatJSON
   *  Azione click button formatta JSON
   */
  clickBtnFormatJSON() {
    this.setJsonParsedForm(this.jsonParsedForm);
  }

  /** clickBtnClose
   *  Azione click button per chiudere modale senza salvataggio
   */
  clickBtnClose() {
    this.closeAndRefresh(false);
  }

  /** clickBtnSave
   *  Azione al click del button save
   */
  clickBtnSave() {
    if (this.form.status === 'VALID' && this.form.touched == true) {
      this.save();
    }
  }

  /** clickBtnReset
   *  Azione click button reset, resetta il form reimpostando i valori iniziali di ingresso
   */
  clickBtnReset() {
    this.resetTextArea(this.json);
  }

}
